import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import NavigationBar from '@/components/NavigationBar'

type Config = {
  children: ReactNode
  onClose: () => void
}

interface ContextValue {
  open: (config: Config) => void
  close: () => void
}

const SuccessScreenContext = createContext<ContextValue>({
  open: () => undefined,
  close: () => undefined
})

export function useSuccessScreen() {
  return useContext(SuccessScreenContext)
}

interface SuccessScreenProviderProps {
  children: ReactNode
}

export function SuccessScreenProvider({ children }: SuccessScreenProviderProps) {
  const { height: deviceHeight } = useWindowDimensions()
  const [config, setConfig] = useState<Config | null>(null)

  const offset = useSharedValue(deviceHeight)
  const open = useCallback((config: Config) => setConfig(config), [])
  const close = useCallback(() => {
    offset.value = withTiming(deviceHeight, { duration: 300 }, () => {
      runOnJS(setConfig)(null)
    })
  }, [])

  return (
    <SuccessScreenContext.Provider value={{ open, close }}>
      {children}
      {config && <SuccessScreen {...config} offset={offset} />}
    </SuccessScreenContext.Provider>
  )
}

function SuccessScreen({ children, onClose, offset }: Config & { offset: SharedValue<number> }) {
  const { t } = useTranslation()
  const viewStyle = useAnimatedStyle(() => {
    return { transform: [{ translateY: offset.value }] }
  })

  useEffect(() => {
    offset.value = withTiming(0, { duration: 500 })
  }, [])

  return (
    <Animated.View style={[styles.overlay, viewStyle]}>
      <NavigationBar iconId={'x'} handleAction={onClose} />
      <View className={'flex-1 items-center bg-background-primary pt-[247px]'}>{children}</View>
      <BottomActionPanel hasTopBorder={false}>
        <Button size={'md'} className={'flex-1'} onPress={onClose}>
          {t('ui:complete')}
        </Button>
      </BottomActionPanel>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 20
  }
})
